import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Capture {
  id: string;
  type: 'photo' | 'video';
  dataUrl: string; // Base64 or blob URL
  thumbnailUrl?: string;
  modelId?: string;
  modelName?: string;
  createdAt: Date;
  duration?: number; // For videos, in seconds
}

interface CapturesState {
  captures: Capture[];
  isLoading: boolean;
  selectedCapture: Capture | null;
  isViewerOpen: boolean;

  // Actions
  addCapture: (capture: Omit<Capture, 'id' | 'createdAt'>) => void;
  deleteCapture: (id: string) => void;
  clearAllCaptures: () => void;
  setSelectedCapture: (capture: Capture | null) => void;
  setViewerOpen: (open: boolean) => void;

  // Computed
  photoCount: () => number;
  videoCount: () => number;
}

const generateId = () => `capture_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const useCapturesStore = create<CapturesState>()(
  persist(
    (set, get) => ({
      captures: [],
      isLoading: false,
      selectedCapture: null,
      isViewerOpen: false,

      addCapture: (capture) => {
        const newCapture: Capture = {
          ...capture,
          id: generateId(),
          createdAt: new Date(),
        };

        set((state) => ({
          captures: [newCapture, ...state.captures].slice(0, 500), // Keep max 500 captures
        }));
      },

      deleteCapture: (id) => {
        set((state) => ({
          captures: state.captures.filter((c) => c.id !== id),
          selectedCapture: state.selectedCapture?.id === id ? null : state.selectedCapture,
        }));
      },

      clearAllCaptures: () => {
        set({ captures: [], selectedCapture: null });
      },

      setSelectedCapture: (capture) => {
        set({ selectedCapture: capture, isViewerOpen: capture !== null });
      },

      setViewerOpen: (open) => {
        set({ isViewerOpen: open, selectedCapture: open ? get().selectedCapture : null });
      },

      // Computed helpers
      photoCount: () => get().captures.filter((c) => c.type === 'photo').length,
      videoCount: () => get().captures.filter((c) => c.type === 'video').length,
    }),
    {
      name: 'visionar-captures',
      partialize: (state) => ({
        captures: state.captures,
      }),
    }
  )
);
